/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronDown, ArrowUp } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useSnapshot } from "valtio";
import { editorState } from "../../utils/editor";
import { shareFeedbackSettings } from "../../apis/settings/share.feedback.settings";

interface Message {
  id: number;
  text: string;
  issueType: string;
  timestamp: string;
}

interface Chat {
  id: string;
  issueType: string;
  messages: Message[];
  createdAt: string;
  lastMessageTime: string;
  status: string;
}

const SendAMessage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { XAccountHash } = useSnapshot(editorState);
  const [message, setMessage] = useState("");
  const [selectedIssue, setSelectedIssue] = useState("ISSUE");
  const [showDropdown, setShowDropdown] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);

  const toast = useToast({
    position: "top",
    containerStyle: {
      zIndex: 9,
    },
  });

  const issueTypes = [
    "ISSUE",
    "Bug Report",
    "Feature Request",
    "General Inquiry",
    "Technical Support",
    "Feedback",
  ];

  // Load existing chat if chatId is provided, or load the most recent active chat
  useEffect(() => {
    if (!XAccountHash) return;

    const storageKey = `user-${XAccountHash}-chats`;
    const existingChats: Chat[] = JSON.parse(
      localStorage.getItem(storageKey) || "[]"
    );

    const chatId = searchParams.get("chatId");

    if (chatId) {
      // Load specific chat by ID
      const chat = existingChats.find((c) => c.id === chatId);
      if (chat) {
        setMessages(chat.messages);
        setSelectedIssue(chat.issueType);
        setCurrentChatId(chatId);
        return;
      }
    }

    // Only auto-load recent chat if no chatId in URL and no messages loaded
    if (!chatId && messages.length === 0 && existingChats.length > 0) {
      // Load the most recent active chat
      const sortedChats = existingChats
        .filter((c) => c.status === "active")
        .sort(
          (a, b) =>
            new Date(b.lastMessageTime).getTime() -
            new Date(a.lastMessageTime).getTime()
        );

      if (sortedChats.length > 0) {
        const recentChat = sortedChats[0];
        setMessages(recentChat.messages);
        setSelectedIssue(recentChat.issueType);
        setCurrentChatId(recentChat.id);
        // Update URL with the chat ID
        navigate(`/send-a-message?chatId=${recentChat.id}`, { replace: true });
      }
    }
  }, [searchParams, XAccountHash, messages.length, navigate]);

  const handleSend = () => {
    if (message.trim() && !isSending) {
      setIsSending(true);

      shareFeedbackSettings({
        headers: {
          "X-Account-Hash": XAccountHash,
        },
        body: {
          feedbackOptions: selectedIssue.toLowerCase().replace(/\s+/g, ""),
          message: message,
        },
        onCompleted() {
          const now = new Date();
          const timestamp = now.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });

          const newMessage: Message = {
            id: Date.now(),
            text: message,
            issueType: selectedIssue,
            timestamp: timestamp,
          };

          const updatedMessages = [...messages, newMessage];
          setMessages(updatedMessages);
          setMessage("");
          setIsSending(false);

          // Save to localStorage
          const storageKey = `user-${XAccountHash}-chats`;
          const existingChats: Chat[] = JSON.parse(
            localStorage.getItem(storageKey) || "[]"
          );

          if (currentChatId) {
            // Update existing chat
            const chatIndex = existingChats.findIndex(
              (chat) => chat.id === currentChatId
            );
            if (chatIndex >= 0) {
              existingChats[chatIndex].messages.push(newMessage);
              existingChats[chatIndex].lastMessageTime =
                new Date().toISOString();
            }
          } else {
            // Create new chat
            const newChatId = `chat-${Date.now()}`;
            existingChats.push({
              id: newChatId,
              issueType: selectedIssue,
              messages: [newMessage],
              createdAt: new Date().toISOString(),
              lastMessageTime: new Date().toISOString(),
              status: "active",
            });
            setCurrentChatId(newChatId);
            // Update URL with the new chat ID
            navigate(`/send-a-message?chatId=${newChatId}`, { replace: true });
          }

          localStorage.setItem(storageKey, JSON.stringify(existingChats));

          toast({
            status: "success",
            description: "Your message was sent successfully",
          });
        },
        onError(err) {
          console.log(err);
          setIsSending(false);

          toast({
            status: "error",
            description: "Failed to send message. Please try again.",
          });
        },
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-[#F9F9F9] font-[glacial] relative pb-4">
      <div className="flex flex-col items-center pt-6 pb-4 border-b border-gray-200 bg-white">
        <button
          onClick={() => {
            if (messages.length > 0) {
              navigate("/messages");
            } else {
              navigate("/support");
            }
          }}
          className="absolute top-6 left-6 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft size={24} className="text-gray-900" />
        </button>

        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
            <span className="text-white text-2xl dcalligraphy">W</span>
          </div>
          <div className="flex flex-col items-center">
            <h1 className="text-base font-[optima] text-gray-900">
              Tola @ Weddn
            </h1>
            {messages.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">Awaiting response...</p>
            )}
          </div>
        </div>

        {messages.length > 0 && (
          <div className="mt-3">
            <span className="px-4 py-1.5 rounded-full bg-[#FFE4E4] text-gray-900 text-xs">
              {messages[0].issueType}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 px-6 py-6 overflow-y-auto">
        {messages.map((msg) => (
          <div key={msg.id} className="mb-6 flex justify-end">
            <div className="bg-white rounded-lg p-4 pb-6 shadow-sm max-w-[85%] relative">
              <p className="text-gray-900 text-sm leading-relaxed whitespace-pre-wrap mb-1">
                {msg.text}
              </p>
              <p className="text-xs text-gray-400 absolute bottom-2 right-3">
                {msg.timestamp}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="px-6 pb-6 pt-4 bg-[#F9F9F9]">
        <div className="relative">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message"
            className="w-full min-h-[60px] max-h-[120px] p-4 pb-12 pr-16 rounded-lg border border-gray-200 bg-white resize-none focus:outline-none focus:border-gray-300 text-gray-900 placeholder:text-gray-400"
          />

          {messages.length === 0 && (
            <div className="absolute bottom-3 left-3">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFE4E4] text-gray-900 text-xs hover:bg-[#ffd4d4] transition-colors"
              >
                <span>{selectedIssue}</span>
                <ChevronDown size={14} />
              </button>

              {showDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  />
                  <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                    {issueTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => {
                          setSelectedIssue(type);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={!message.trim() || isSending}
            className={`absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              message.trim() && !isSending
                ? "bg-[#1E3A8A] hover:bg-[#1e40af]"
                : "bg-gray-300"
            }`}
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowUp
                size={20}
                className={message.trim() ? "text-white" : "text-gray-500"}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendAMessage;
